import Type, {} from 'typebox';
import { ConfigSchema } from './common.js';
import { MAX_LENGTH } from './constants.js';
import { DiscriminatedUnion } from './utils/typebox.js';
/**
 * Where the client runs, e.g. `delta-web`, `tv-web`, `apple-tv`,
 * `android-tv`, `smart-tv`. Sent once in the handshake and stamped on every
 * chat message the socket sends, for the BigQuery export. Free-form
 * kebab-case so a new surface does not need a server release.
 */
export const ChatCtxSchema = Type.String({
    pattern: '^[a-z0-9]+(-[a-z0-9]+)*$',
    maxLength: 32,
});
export const AuthHandshakeSchema = Type.Object({
    idToken: Type.Optional(Type.Union([Type.Null(), Type.String()])),
    accessToken: Type.Optional(Type.Union([Type.Null(), Type.String()])),
    chatCtx: Type.Optional(ChatCtxSchema),
});
export { MAX_LENGTH };
export const ClientChatMessageSchema = Type.Object({
    type: Type.Literal('CHAT_MESSAGE'),
    text: Type.String({ minLength: 1, maxLength: MAX_LENGTH }),
});
export const ClientMessageSchema = Type.Union([ClientChatMessageSchema]);
export const ConfigSharedSchema = Type.Object({
    type: Type.Literal('CONFIG'),
    id: Type.String(),
    config: ConfigSchema,
    ts: Type.Number(),
});
export const UserSharedSchema = Type.Object({
    type: Type.Literal('USER'),
    id: Type.String(),
    user: Type.Optional(Type.Object({
        userId: Type.String(),
        viaUserId: Type.Optional(Type.String()),
        banInfo: Type.Optional(Type.Object({
            expiresAt: Type.Union([Type.Number(), Type.Null()]),
            // Maybe they should know the reason, and the issuer?
        })),
        anonymousIp: Type.Optional(Type.Boolean()),
    })),
    ts: Type.Number(),
});
export const ClearChatSharedSchema = Type.Object({
    type: Type.Literal('CLEAR_CHAT'),
    id: Type.String(),
    issuedBy: Type.String(),
    ts: Type.Number(),
    role: Type.String(),
});
export const RedactMessageSharedSchema = Type.Object({
    type: Type.Literal('REDACT_MESSAGE'),
    id: Type.String(),
    messageIds: Type.Array(Type.String()),
    issuedBy: Type.String(),
    ts: Type.Number(),
    role: Type.String(),
});
export const BanUserSharedSchema = Type.Object({
    type: Type.Literal('BAN_USER'),
    id: Type.String(),
    userIds: Type.Array(Type.String()),
    reason: Type.Optional(Type.String()),
    issuedBy: Type.String(),
    expiresAt: Type.Union([Type.Null(), Type.Number()]),
    ts: Type.Number(),
    role: Type.String(),
});
export const UnbanUserSharedSchema = Type.Object({
    type: Type.Literal('UNBAN_USER'),
    id: Type.String(),
    userIds: Type.Array(Type.String()),
    issuedBy: Type.String(),
    ts: Type.Number(),
    role: Type.String(),
});
export const SystemMessageSchema = Type.Object({
    type: Type.Literal('SYSTEM_MESSAGE'),
    id: Type.String(),
    what: Type.Optional(Type.String()),
    contentType: Type.Union([Type.Literal('text'), Type.Literal('markdown')]),
    content: Type.String(),
    user: Type.Optional(Type.Object({ name: Type.String() })),
    /** With `what: "rateLimited"`: milliseconds until the next message is taken. */
    retryAfterMs: Type.Optional(Type.Number({ minimum: 0 })),
    ts: Type.Number(),
});
export const ChatMessagePublicSchema = Type.Object({
    type: Type.Literal('CHAT_MESSAGE'),
    id: Type.String(),
    text: Type.String({ minLength: 1, maxLength: 256 }),
    user: Type.Object({ id: Type.String(), name: Type.String() }),
    ts: Type.Number(),
    role: Type.String(),
    /**
     * Set on the copy that goes to the sender's own account: every socket
     * logged in as the sender, as the parent the sender acts through, or as
     * another child profile of that parent. Absent on the public broadcast.
     * A client marks messages with this flag as its own. It is set on every
     * account copy, accepted or held back, so it never reveals moderation.
     */
    echo: Type.Optional(Type.Literal(true, {
        description: "Present on the copy sent to the sockets of the sender's own NRK account (the sender, the parent a child profile acts through, and sibling profiles). Absent on the copy everyone else gets. Mark a message with `echo` as your own; it is set on every account copy, whether the message was accepted or held back.",
    })),
});
export const ChatMessageAdminSchema = Type.Object({
    type: Type.Literal('CHAT_MESSAGE'),
    id: Type.String(),
    text: Type.String({ minLength: 1, maxLength: 256 }),
    user: Type.Object({
        id: Type.String(),
        viaUserId: Type.Optional(Type.String()),
        name: Type.String(),
        birthYear: Type.Optional(Type.Number()),
    }),
    ts: Type.Number(),
    role: Type.String(),
    chatCtx: Type.Optional(ChatCtxSchema),
});
export const MutedChatMessageAdminSchema = Type.Object({
    type: Type.Literal('MUTED_CHAT_MESSAGE'),
    id: Type.String(),
    text: Type.String({ minLength: 1, maxLength: 256 }),
    user: Type.Object({
        id: Type.String(),
        viaUserId: Type.Optional(Type.String()),
        name: Type.String(),
        birthYear: Type.Optional(Type.Number()),
    }),
    ts: Type.Number(),
    role: Type.String(),
    chatCtx: Type.Optional(ChatCtxSchema),
});
export const MuteUserAdminSchema = Type.Object({
    type: Type.Literal('MUTE_USER'),
    id: Type.String(),
    userIds: Type.Array(Type.String()),
    issuedBy: Type.String(),
    expiresAt: Type.Number(),
    ts: Type.Number(),
    role: Type.String(),
});
export const UnmuteUserAdminSchema = Type.Object({
    type: Type.Literal('UNMUTE_USER'),
    id: Type.String(),
    userIds: Type.Array(Type.String()),
    issuedBy: Type.String(),
    ts: Type.Number(),
    role: Type.String(),
});
/**
 * The second moderation pass on a message automatic moderation held back:
 * which severe categories it hit. Admin only, stored in the event stream so
 * the moderator feed can show it on reload. Never sent to the public.
 */
export const ModerationSeverityAdminSchema = Type.Object({
    type: Type.Literal('MODERATION_SEVERITY'),
    id: Type.String(),
    messageId: Type.String(),
    userId: Type.String(),
    /** Categories at or above the severity threshold, never empty. */
    categories: Type.Array(Type.String(), { minItems: 1 }),
    /** Probability per category, also the ones below the threshold. */
    scores: Type.Record(Type.String(), Type.Number()),
    moderator: Type.String(),
    issuedBy: Type.String(),
    ts: Type.Number(),
    role: Type.String(),
});
/**
 * A moderator changed the polls or the sticky messages. The other
 * moderators' pages fetch them again over HTTP. Not stored: a page that
 * connects fetches them anyway.
 */
export const PollsChangedAdminSchema = Type.Object({
    type: Type.Literal('POLLS_CHANGED'),
    ts: Type.Number(),
});
export const StickyMessagesChangedAdminSchema = Type.Object({
    type: Type.Literal('STICKY_MESSAGES_CHANGED'),
    ts: Type.Number(),
});
export const PublicMessageSchema = DiscriminatedUnion([ChatMessagePublicSchema], { discriminator: 'type' });
export const AdminMessageSchema = DiscriminatedUnion([
    ChatMessageAdminSchema,
    MutedChatMessageAdminSchema,
    MuteUserAdminSchema,
    UnmuteUserAdminSchema,
    ModerationSeverityAdminSchema,
    PollsChangedAdminSchema,
    StickyMessagesChangedAdminSchema,
], {
    discriminator: 'type',
});
export const SharedMessageSchema = DiscriminatedUnion([
    ConfigSharedSchema,
    UserSharedSchema,
    ClearChatSharedSchema,
    RedactMessageSharedSchema,
    BanUserSharedSchema,
    UnbanUserSharedSchema,
    SystemMessageSchema,
], { discriminator: 'type' });
export const ReconnectEventSchema = DiscriminatedUnion([
    Type.Object({ room: Type.Literal('PUBLIC'), event: PublicMessageSchema }),
    Type.Object({ room: Type.Literal('SHARED'), event: SharedMessageSchema }),
], { discriminator: 'room' });
export const UpdateBanInfoSchema = Type.Object({
    type: Type.Literal('UPDATE_BANINFO'),
    userIds: Type.Array(Type.String()),
});
export const UpdateMuteInfoSchema = Type.Object({
    type: Type.Literal('UPDATE_MUTEINFO'),
    userIds: Type.Array(Type.String()),
});
export const AdminToServerMessageSchema = DiscriminatedUnion([UpdateBanInfoSchema, UpdateMuteInfoSchema], { discriminator: 'type' });
//# sourceMappingURL=socket.js.map